import { defineStore } from 'pinia'
import { ref, computed, watch, markRaw, shallowRef } from 'vue'
import MapInfo from '@/model/MapInfo'
import StoryTree from '@/model/StoryTree'
import {
  hasBlockedReadonlyStructureInSelection,
  resolveSelectedEventIndices,
  resolveSelectionAfterRemove,
} from '@/events/list/resolveSelectedEventIndices'
import {
  canMoveEventsAt,
  insertEventsAtPlacement,
  resolveInsertPlacementAfterIndex,
  resolveInsertPlacementFromAddPlaceholder,
  resolveInsertPlacementFromDrop,
  resolveInsertPlacementFromSelection,
  validateEventInsert,
  type InsertPlacement,
  type ValidateEventInsertResult,
} from '@/events/list/eventListOperations'
import type EventBase from '@/model/EventClass/EventBase'
import EventID from '@/model/EventClass/EventID'
import { createEmptyEventByCode, ensureEventInstance, ensureEventListInstances } from '@/model/EventClass/eventFromReader'
import EventPicShow from '@/model/EventClass/EventPicShow'
import EventButtonDif from '@/model/EventClass/EventButtonDif'
import EventButtonDifChoose from '@/model/EventClass/EventButtonDifChoose'
import EventButtonDifEnd from '@/model/EventClass/EventButtonDifEnd'
import EventIF from '@/model/EventClass/EventIF'
import EventIFEnd from '@/model/EventClass/EventIFEnd'
import EventIfChoice from '@/model/EventClass/EventIfChoice'
import EventLoop from '@/model/EventClass/EventLoop'
import EventLoopAboveStart from '@/model/EventClass/EventLoopAboveStart'
import EventTextChoice from '@/model/EventClass/EventTextChoice'
import EventTextDif from '@/model/EventClass/EventTextDif'
import EventCallSubStory from '@/model/EventClass/EventCallSubStory'
import EventJumpStory from '@/model/EventClass/EventJumpStory'
import EventText from '@/model/EventClass/EventText'
import EventTextEnd from '@/model/EventClass/EventTextEnd'
import { resolveDialogRoleColor } from '@/utils/textColor'
import CustomUIData from '@/model/CustomUI/CustomUIData'
import CustomUIItem from '@/model/CustomUI/CustomUIItem'
import { hydrateIntoInstance } from '@/model/jsonHydrator'
import {
  expandStructureBlocksInIndexRange,
  findFoldableEndIndex,
  findStructureBlockEndIndex,
  getEventSelectionRange,
  getStructureBlockRange,
  getStructureSelectionContext,
  isChoiceBlockHeaderCode,
  isStructureHeaderCode,
  isStructureReadonlyCode,
  rebuildFoldIndent,
} from '@/events/list/structureRules'
import { setPicShowPreset } from '@/features/event/picShowPreset'
import { resolveStoredPathFromDisplayPath } from '@/features/virtual-resource/fileNamePath'
import {
  captureEventHistoryItems,
  captureEventHistoryRange,
  captureEventHistoryRangeSnapshot,
  captureEventHistoryRanges,
  captureMapHistoryBundle,
  captureMapHistorySnapshot,
  cloneHistoryEvents,
  createEditorHistory,
  type EditorHistoryDomain,
  type EditorHistoryEventListResolver,
  type EditorHistorySelection,
  type PendingEventUpdateHistory,
} from '@/features/history/editorHistory'
import { useWorkspaceStore } from '@/store/workspace'
import {
  addStoryMapChild,
  addStoryMapSiblingAfter,
  applyStoryMapDrop,
  deleteMapCascade,
  canDeleteMapCascade,
  isStrictDescendantOf,
  renameStoryMap,
} from '@/control/storyMapOperations'
import type { EventPaletteItem } from '@/events/palette/eventPalette'
import {
  mapInfoSubtreeToStoryNodes,
  sceneTreeLabel,
  sortStoryMaps,
  type StoryTreeNode,
} from '@/features/story/storyTreeProjection'
import {
  applyAdvancedTextImport,
  applyBasicTextImport,
  type AdvancedTextImportRow,
  type BasicTextImportRow,
  type TextImportApplyResult,
} from '@/features/text-import/textImportLogic'

/**
 * 场景 / 事件编辑器共享状态
 *
 * 职责：
 * 1. 管理当前选中的场景 ID 和事件下标（多个组件共享）
 * 2. 维护场景树与事件列表的变更修订号（用于在 markRaw 下通知组件刷新）
 * 3. 封装剧情增删拖拽、事件插入等业务操作
 *
 * 设计原则：
 * - 工程数据 (MainProject) 始终只保存一份，存于 workspace store
 * - 本 store 仅存引用和 UI 状态，不复制工程数据
 */
export const useSceneEditorStore = defineStore('sceneEditor', () => {
  const workspace = useWorkspaceStore()
  const isDev = typeof import.meta !== 'undefined' && Boolean(import.meta.env?.DEV)
  type EventClipboardState = {
    mode: 'copy' | 'cut'
    sourceMapId: number | null
    events: unknown[]
  }
  type StoryClipboardMap = {
    sourceId: number
    sourceParentId: number
    name: string
    order: number
    isHard: boolean
    events: unknown[]
  }
  type StoryClipboardState = {
    mode: 'copy' | 'cut'
    maps: StoryClipboardMap[]
  }
  type AddPlaceholderSelection = {
    key: string
    insertAt: number
    previousIndex: number
  }
  type CustomUiEventSource = 'none' | 'load' | 'after' | 'control'
  type CustomUiSelection = {
    uiIndex: number
    itemIndex: number | null
    eventSource: CustomUiEventSource
  }
  type StoryTreeIndex = {
    list: MapInfo[]
    byId: Map<number, MapInfo>
    parentById: Map<number, number>
    nodes: StoryTreeNode[]
  }

  /* ——— 选中状态 ——— */
  /** 当前选中的场景（剧情）ID，null 表示未选中 */
  const selectedSceneId = ref<number | null>(null)
  /** 当前选中的事件在事件列表中的下标，null 表示未选中 */
  const selectedEventIndex = ref<number | null>(null)
  /** 当前选中的事件对象引用，切换选中时直接写入，避免同下标换事件时属性面板读到旧对象。 */
  const selectedEvent = shallowRef<EventBase | null>(null)
  /** 当前选中的事件下标集合，支持 Ctrl/Shift 多选。 */
  const selectedEventIndices = ref(new Set<number>())
  /** Shift 连续选择的锚点。 */
  const selectedEventAnchorIndex = ref<number | null>(null)
  /** 当前选中的空事件占位（仅用于插入位置，不属于真实事件数据）。 */
  const selectedAddPlaceholder = ref<AddPlaceholderSelection | null>(null)
  /** 当前选中的高级 UI 节点；为空时表示仍在剧情编辑上下文。 */
  const selectedCustomUi = ref<CustomUiSelection | null>(null)
  /** 事件剪贴板：复制保留引用；剪切立即移出列表，粘贴时再插入。 */
  const eventClipboard = ref<EventClipboardState | null>(null)
  /** 剧情剪贴板：保存剧情子树快照，粘贴时重新分配剧情 ID。 */
  const storyClipboard = ref<StoryClipboardState | null>(null)

  /* ——— 修订号：markRaw 下用于触发组件刷新 ——— */
  /** 兼容旧组件的事件修订号；后续新链路优先使用更细粒度 revision。 */
  const sceneEventsRevision = ref(0)
  /** 事件结构发生增删、折叠、分支同步等变化时自增，普通属性值变化不能使用它。 */
  const sceneStructureRevision = ref(0)
  /** 事件预览内容发生变化时自增。 */
  const scenePreviewRevision = ref(0)
  /** 事件列表文案或颜色发生变化时自增。 */
  const sceneEventLabelRevision = ref(0)
  /** 剧情树结构发生增删 / 拖拽重排时自增 */
  const storyStructureRevision = ref(0)
  /** 高级 UI 列表、控件、属性或事件发生变化时自增。 */
  const customUiRevision = ref(0)

  /* ——— 折叠块管理 ——— */
  /** 记录被折叠的「块头」行下标集合 */
  const collapsedFoldIndices = ref(new Set<number>())

  /* ——— 场景树展开状态 ——— */
  const expandedSceneKeys = ref<Array<string | number>>([])

  /* ——— 拖拽中的节点 ——— */
  const storyDragFromId = ref<number | null>(null)
  const history = createEditorHistory()
  let pendingEventHistory: PendingEventUpdateHistory | null = null
  let pendingEventHistoryTimer: ReturnType<typeof setTimeout> | null = null

  /* ——— 工程引用 ——— */
  const project = computed(() => workspace.project)

  const emptyStoryTreeIndex: StoryTreeIndex = markRaw({
    list: [],
    byId: new Map<number, MapInfo>(),
    parentById: new Map<number, number>(),
    nodes: [],
  })

  const storyTreeIndex = computed(() => {
    storyStructureRevision.value
    const p = project.value
    const st = getStoryTree()
    if (!p || !st) return emptyStoryTreeIndex
    const list = sortStoryMaps(p.Storys() as MapInfo[])
    const byId = new Map<number, MapInfo>()
    const parentById = new Map<number, number>()
    for (const item of list) {
      byId.set(item.Id, item)
      parentById.set(item.Id, item.ParentId)
    }
    const roots = st.rootMaps as MapInfo[] | undefined
    return markRaw({
      list,
      byId,
      parentById,
      nodes: roots?.length ? mapInfoSubtreeToStoryNodes(roots) : [],
    })
  })

  /* ——— 场景列表（排序后） ——— */
  const storyTreeList = computed(() => storyTreeIndex.value.list)

  /* ——— 父子映射表（用于展开祖先节点） ——— */
  const storyParentMap = computed(() => storyTreeIndex.value.parentById)

  /* ——— 当前选中的 MapInfo 引用 ——— */
  const selectedMap = computed(() => {
    const id = selectedSceneId.value
    if (id === null) return null
    return storyTreeIndex.value.byId.get(id) ?? null
  })

  /* ——— 当前场景的事件列表（引用，非拷贝） ——— */
  const selectedSceneEvents = computed((): EventBase[] => {
    sceneStructureRevision.value
    storyStructureRevision.value
    const st = getStoryTree()
    const id = selectedSceneId.value
    if (!st || id === null) return []
    return st.loadEvent(id) as EventBase[]
  })

  const eventObjectKeys = new WeakMap<EventBase, number>()
  let nextEventObjectKey = 1

  function getEventObjectKey(event: EventBase): number {
    const existing = eventObjectKeys.get(event)
    if (existing) return existing
    const next = nextEventObjectKey
    nextEventObjectKey += 1
    eventObjectKeys.set(event, next)
    return next
  }

  /** 属性面板 key：由选中下标和事件对象身份共同决定，避免同下标换事件时复用旧属性面板。 */
  const selectedEventPropertyKey = computed(() => {
    const index = selectedEventIndex.value
    const event = selectedEvent.value
    if (index === null || !event) return 'none'
    return `${index}:${getEventObjectKey(event)}`
  })

  const hasEventClipboard = computed(() => !!eventClipboard.value?.events.length)
  const hasStoryClipboard = computed(() => !!storyClipboard.value?.maps.length)

  const canUndoEditHistory = computed(() => {
    history.revision.value
    return history.undoStack.length > 0
  })

  const canRedoEditHistory = computed(() => {
    history.revision.value
    return history.redoStack.length > 0
  })

  const storyTreeData = computed(() => {
    return storyTreeIndex.value.nodes
  })

  const customUiList = computed(() => {
    customUiRevision.value
    return project.value?.systemData?.customUI?.CUIList ?? []
  })

  const selectedCustomUiData = computed(() => {
    const selection = selectedCustomUi.value
    if (!selection) return null
    return customUiList.value[selection.uiIndex] ?? null
  })

  const selectedCustomUiItem = computed(() => {
    const selection = selectedCustomUi.value
    const data = selectedCustomUiData.value
    if (!selection || !data || selection.itemIndex === null) return null
    return data.ControlList[selection.itemIndex] ?? null
  })

  const selectedEventList = computed((): EventBase[] => {
    const selection = selectedCustomUi.value
    const data = selectedCustomUiData.value
    if (!selection || !data || selection.eventSource === 'none') return selectedSceneEvents.value
    if (selection.eventSource === 'load') return data.LoadEvent as unknown as EventBase[]
    if (selection.eventSource === 'after') return data.AfterEvent as unknown as EventBase[]
    const item = selectedCustomUiItem.value
    return item?.Type === CustomUIItem.controlType.Button ? item.EventList as unknown as EventBase[] : []
  })

  /** 获取 storyTree 并断言回原始类型（markRaw 后 Pinia 推断的结构类型与 class 不兼容） */
  function getStoryTree(): StoryTree | null {
    return (project.value?.storyTree as StoryTree) ?? null
  }

  function captureHistorySelection(): EditorHistorySelection {
    return {
      sceneId: selectedCustomUi.value
        ? getCustomUiSelectionHistoryId(selectedCustomUi.value)
        : selectedSceneId.value,
      eventIndex: selectedEventIndex.value,
    }
  }

  const resolveHistoryEventList: EditorHistoryEventListResolver = (mapId) => {
    const customSelection = parseCustomUiHistoryId(mapId)
    if (!customSelection) return null
    const data = customUiList.value[customSelection.uiIndex]
    if (!data) return null
    if (customSelection.eventSource === 'load') return data.LoadEvent as unknown as EventBase[]
    if (customSelection.eventSource === 'after') return data.AfterEvent as unknown as EventBase[]
    if (customSelection.eventSource === 'control' && customSelection.itemIndex !== null) {
      const item = data.ControlList[customSelection.itemIndex]
      return item?.Type === CustomUIItem.controlType.Button ? item.EventList as unknown as EventBase[] : null
    }
    return null
  }

  function cloneCustomUiListSnapshot(): unknown[] {
    return JSON.parse(JSON.stringify(customUiList.value)) as unknown[]
  }

  function applyCustomUiListSnapshot(snapshot: unknown[]): void {
    const nextList = snapshot.map((item) =>
      hydrateIntoInstance(new CustomUIData(), item, ['customUI', 'CUIList']),
    )
    const list = customUiList.value
    list.splice(0, list.length, ...nextList)
    bumpSceneStructureRevision(true)
  }

  /**
   * 记录高级 UI 结构或属性修改快照。
   * @param beforeList 修改前的 `systemData.customUI.CUIList` 快照。
   * @param label 历史记录显示文案。
   */
  function commitCustomUiHistory(beforeList: unknown[], label: string): void {
    workspace.markProjectDirty()
    history.push({
      domain: 'customUi',
      action: 'update',
      label,
      beforeSelection: captureHistorySelection(),
      afterSelection: captureHistorySelection(),
      beforeList,
      afterList: cloneCustomUiListSnapshot(),
    })
  }

  function getDirectStoryParentId(map: MapInfo, ids: Set<number>): number {
    return map.ParentId <= 0 || map.ParentId === map.Id || !ids.has(map.ParentId) ? 0 : map.ParentId
  }

  function getStoryMoveAffectedMapIds(dragId: number, targetId: number, dropPosition: 'before' | 'inside' | 'after'): number[] {
    const st = getStoryTree()
    if (!st) return []
    const ids = new Set(st.Maps.map((map) => map.Id))
    const drag = st.Maps.find((map) => map.Id === dragId)
    const target = st.Maps.find((map) => map.Id === targetId)
    if (!drag || !target) return []
    const oldParentId = getDirectStoryParentId(drag, ids)
    const nextParentId = dropPosition === 'inside' ? targetId : getDirectStoryParentId(target, ids)
    return st.Maps
      .filter((map) => {
        const parentId = getDirectStoryParentId(map, ids)
        return map.Id === dragId || parentId === oldParentId || parentId === nextParentId
      })
      .map((map) => map.Id)
  }

  function captureStoryMapSnapshots(ids: number[]) {
    const st = getStoryTree()
    if (!st) return []
    const wanted = new Set(ids)
    return st.Maps
      .filter((map) => wanted.has(map.Id))
      .map((map) => captureMapHistorySnapshot(map))
  }

  function captureStorySubtreeBundles(rootId: number) {
    const st = getStoryTree()
    if (!st) return []
    st.createStoryTree()
    const root = st.Maps.find((map) => map.Id === rootId)
    if (!root) return []
    const bundles: ReturnType<typeof captureMapHistoryBundle>[] = []
    const walk = (map: MapInfo) => {
      bundles.push(captureMapHistoryBundle(st, map))
      for (const child of map.Childs ?? []) walk(child)
    }
    walk(root)
    return bundles
  }

  function captureStoryClipboardMaps(rootId: number): StoryClipboardMap[] {
    const st = getStoryTree()
    if (!st) return []
    st.createStoryTree()
    const root = st.Maps.find((map) => map.Id === rootId)
    if (!root) return []
    const maps: StoryClipboardMap[] = []
    const walk = (map: MapInfo) => {
      const events = st.Events[String(map.Id)] ?? map.List ?? []
      maps.push({
        sourceId: map.Id,
        sourceParentId: map.ParentId,
        name: map.Name,
        order: map.Order,
        isHard: map.IsHard,
        events: events.map((event) => JSON.parse(JSON.stringify(event)) as unknown),
      })
      for (const child of map.Childs ?? []) walk(child)
    }
    walk(root)
    return maps
  }

  function applyHistorySelection(selection: EditorHistorySelection, domain: EditorHistoryDomain, restoredEventList = false): void {
    const customSelection = selection.sceneId === null ? null : parseCustomUiHistoryId(selection.sceneId)
    if (domain === 'story') storyStructureRevision.value++
    bumpSceneStructureRevision(domain === 'customUi' || Boolean(customSelection))
    if (customSelection) {
      selectCustomUi(customSelection)
    } else {
      selectedCustomUi.value = null
      selectedSceneId.value = selection.sceneId
      expandSceneAncestors(selection.sceneId)
    }
    const list = selectedEventList.value
    if (!restoredEventList) {
      syncEventStructureState(list)
    } else {
      collapsedFoldIndices.value = new Set(
        list
          .map((event, index) => (Number(event?.isFold ?? 0) > 0 ? index : -1))
          .filter((index) => index >= 0),
      )
    }
    const index = selection.eventIndex
    selectEventAtIndex(index !== null && index >= 0 && index < list.length ? index : null)
  }

  function cancelPendingEventHistory(): void {
    if (pendingEventHistoryTimer !== null) {
      window.clearTimeout(pendingEventHistoryTimer)
      pendingEventHistoryTimer = null
    }
    pendingEventHistory = null
  }

  function syncEventStructureState(events: EventBase[]) {
    collapsedFoldIndices.value = rebuildFoldIndent(events)
  }

  function bumpSceneStructureRevision(isCustomUi = Boolean(selectedCustomUi.value)): void {
    sceneStructureRevision.value++
    sceneEventLabelRevision.value++
    scenePreviewRevision.value++
    sceneEventsRevision.value++
    if (isCustomUi) customUiRevision.value++
  }

  function bumpSceneValueRevision(isCustomUi = Boolean(selectedCustomUi.value)): void {
    sceneEventLabelRevision.value++
    scenePreviewRevision.value++
    sceneEventsRevision.value++
    if (isCustomUi) customUiRevision.value++
  }

  /* ——— 确保场景事件列表已挂到 storyTree.Events ——— */
  function ensureSceneEventList(st: StoryTree, map: MapInfo): EventBase[] {
    const key = String(map.Id)
    let bucket = st.Events[key]
    if (!bucket) {
      bucket = map.List?.length ? map.List : []
    }
    const normalized = ensureEventListInstances(bucket)
    st.Events[key] = normalized
    map.List = normalized
    return normalized
  }

  function assertCurrentSceneEventReferences(tag: string) {
    if (!isDev) return
    const st = getStoryTree()
    const map = selectedMap.value
    const sceneId = selectedSceneId.value
    if (!st || !map || sceneId === null) return
    const key = String(sceneId)
    const storeBucket = st.Events[key]
    const mapBucket = map.List
    const selectedBucket = selectedSceneEvents.value
    if (storeBucket !== mapBucket || mapBucket !== selectedBucket) {
      throw new Error(`当前剧情事件引用不一致：${tag} / ${sceneId}`)
    }
  }

  /**
   * 当前选中落在文本/图片选项块内时，返回该结构块结束行下标，供画布拖入等同“追加一块”使用。
   */
  function resolveChoiceBlockInsertAfterIndex(
    events: EventBase[],
    selectedIndex: number | null,
  ): number {
    if (selectedIndex == null || selectedIndex < 0 || selectedIndex >= events.length) {
      return Math.max(-1, events.length - 1)
    }
    const context = getStructureSelectionContext(events, selectedIndex)
    if (context.kind === 'none') return selectedIndex
    const headerCode = events[context.headerIndex]?.Code ?? -1
    if (!isChoiceBlockHeaderCode(headerCode)) return selectedIndex
    return context.endIndex
  }

  function resolveActiveInsertPlacement(
    events: EventBase[],
    selectedIndex: number | null,
  ): InsertPlacement {
    const placeholder = selectedAddPlaceholder.value
    if (placeholder) {
      return resolveInsertPlacementFromAddPlaceholder(placeholder.insertAt, events.length)
    }
    return resolveInsertPlacementFromSelection(events, selectedIndex)
  }

  type ActiveEventEditContext = {
    list: EventBase[]
    map: MapInfo | null
    historyMapId: number
    isCustomUi: boolean
  }

  const CUSTOM_UI_HISTORY_BASE = 1_000_000
  const CUSTOM_UI_HISTORY_STEP = 10_000
  const CUSTOM_UI_HISTORY_ROOT = 0
  const CUSTOM_UI_HISTORY_LOAD = 1
  const CUSTOM_UI_HISTORY_AFTER = 2
  const CUSTOM_UI_HISTORY_CONTROL_OFFSET = 100

  function buildCustomUiHistoryId(uiIndex: number, kind: number, itemIndex = 0): number {
    return -(CUSTOM_UI_HISTORY_BASE + uiIndex * CUSTOM_UI_HISTORY_STEP + kind + itemIndex)
  }

  function parseCustomUiHistoryId(id: number): CustomUiSelection | null {
    if (id >= 0) return null
    const raw = Math.abs(id) - CUSTOM_UI_HISTORY_BASE
    if (raw < 0) return null
    const uiIndex = Math.floor(raw / CUSTOM_UI_HISTORY_STEP)
    const offset = raw % CUSTOM_UI_HISTORY_STEP
    if (offset === CUSTOM_UI_HISTORY_ROOT) return { uiIndex, itemIndex: null, eventSource: 'none' }
    if (offset === CUSTOM_UI_HISTORY_LOAD) return { uiIndex, itemIndex: null, eventSource: 'load' }
    if (offset === CUSTOM_UI_HISTORY_AFTER) return { uiIndex, itemIndex: null, eventSource: 'after' }
    if (offset >= CUSTOM_UI_HISTORY_CONTROL_OFFSET) {
      return {
        uiIndex,
        itemIndex: offset - CUSTOM_UI_HISTORY_CONTROL_OFFSET,
        eventSource: 'control',
      }
    }
    return null
  }

  function getCustomUiSelectionHistoryId(selection: CustomUiSelection | null): number | null {
    if (!selection) return null
    if (selection.eventSource === 'load') return buildCustomUiHistoryId(selection.uiIndex, CUSTOM_UI_HISTORY_LOAD)
    if (selection.eventSource === 'after') return buildCustomUiHistoryId(selection.uiIndex, CUSTOM_UI_HISTORY_AFTER)
    if (selection.eventSource === 'control' && selection.itemIndex !== null) {
      return buildCustomUiHistoryId(selection.uiIndex, CUSTOM_UI_HISTORY_CONTROL_OFFSET, selection.itemIndex)
    }
    return buildCustomUiHistoryId(selection.uiIndex, CUSTOM_UI_HISTORY_ROOT)
  }

  function getActiveEventEditContext(): ActiveEventEditContext | null {
    if (selectedCustomUi.value) {
      const historyMapId = getCustomUiSelectionHistoryId(selectedCustomUi.value)
      if (historyMapId === null) return null
      return {
        list: selectedEventList.value,
        map: null,
        historyMapId,
        isCustomUi: true,
      }
    }
    const st = getStoryTree()
    const map = selectedMap.value
    if (!st || !map) return null
    return {
      list: ensureSceneEventList(st, map as MapInfo),
      map: map as MapInfo,
      historyMapId: map.Id,
      isCustomUi: false,
    }
  }

  function touchActiveEventList(context: ActiveEventEditContext): void {
    syncEventStructureState(context.list)
    bumpSceneStructureRevision(context.isCustomUi)
    workspace.markProjectDirty()
    if (!context.isCustomUi) {
      assertCurrentSceneEventReferences('touchActiveEventList')
    }
  }

  function getTextChoiceBlockChoices(headerIndex: number): string[] {
    const list = selectedSceneEvents.value
    const header = list[headerIndex]
    if (!(header instanceof EventTextDif) && header?.Code !== EventID.TextDif) {
      return []
    }
    const choiceIndent = Math.max(0, header?.Indent ?? 0) + 1
    const endExclusive = findStructureBlockEndIndex(list, headerIndex) + 1
    const items: string[] = []
    for (let index = headerIndex + 1; index < endExclusive; index += 1) {
      const event = list[index]
      if (!event || event.Code !== EventID.TextChoice || event.Indent !== choiceIndent) continue
      items.push(String((event as EventTextChoice).Content ?? ''))
    }
    if (items.length) return items
    const fallback = (header as EventTextDif).TextDifChoice ?? []
    return fallback.map((item) => String(item ?? ''))
  }

  function getDefaultTextChoiceLabel(index: number): string {
    return `选项${index + 1}`
  }

  type ButtonChoiceItem = [buttonIndex: string, x: string, y: string]

  function getDefaultButtonChoiceItem(index: number): ButtonChoiceItem {
    return [String(index), '0', '0']
  }

  function stringifyButtonChoiceItem(item: ButtonChoiceItem): string {
    return `${item[0]},${item[1]},${item[2]}`
  }

  function normalizeButtonChoiceItems(rawItems: string[]): ButtonChoiceItem[] {
    const normalized = rawItems
      .slice(0, 64)
      .map((item, index) => {
        const parsed = String(item ?? '').split(',')
        const fallback = getDefaultButtonChoiceItem(index)
        return [
          String(parsed[0] ?? fallback[0]).trim() || fallback[0],
          String(parsed[1] ?? fallback[1]).trim() || fallback[1],
          String(parsed[2] ?? fallback[2]).trim() || fallback[2],
        ] as ButtonChoiceItem
      })
    while (normalized.length < 1) {
      normalized.push(getDefaultButtonChoiceItem(normalized.length))
    }
    return normalized
  }

  function getDefaultButtonChoiceName(index: number): string {
    return `选项${index + 1}`
  }

  function normalizeTextChoiceValues(rawChoices: string[]): string[] {
    const normalizedChoices = rawChoices
      .slice(0, 12)
      .map((item, index) => {
        const text = String(item ?? '').trim()
        return text || getDefaultTextChoiceLabel(index)
      })
    while (normalizedChoices.length < 1) {
      normalizedChoices.push(getDefaultTextChoiceLabel(normalizedChoices.length))
    }
    return normalizedChoices
  }

  function getStructureEndExclusive(list: EventBase[], headerIndex: number): number {
    return getStructureBlockRange(list, headerIndex)?.endExclusive ?? (headerIndex + 1)
  }

  function syncLoopBlockAt(
    list: EventBase[],
    mapId: string,
    headerIndex: number,
  ): boolean {
    const header = list[headerIndex]
    if (!(header instanceof EventLoop) && header?.Code !== EventID.Loop) {
      return false
    }
    const typedHeader = header as EventLoop
    typedHeader.FoldIndent = typedHeader.Indent

    const endExclusive = getStructureEndExclusive(list, headerIndex)
    const currentBlock = list.slice(headerIndex + 1, endExclusive)
    const headerIndent = Math.max(0, typedHeader.Indent ?? 0)
    const currentEnd = currentBlock.find((item) => {
      return item?.Code === EventID.LoopAboveStart && Math.max(0, item.Indent ?? 0) === headerIndent
    }) as EventLoopAboveStart | undefined
    const bodyEvents = currentEnd ? currentBlock.filter((item) => item !== currentEnd) : currentBlock
    const hasMatchingEnd = !!currentEnd
      && currentEnd.mapId === (typedHeader.mapId || mapId)
      && currentEnd.FoldIndent === 0
      && currentEnd.SeniorFold === false
      && currentEnd.isFold === 0
      && currentBlock.length === bodyEvents.length + 1
    if (hasMatchingEnd) {
      return false
    }

    const endEvent = currentEnd ?? (createEmptyEventByCode(EventID.LoopAboveStart) as EventLoopAboveStart)
    endEvent.mapId = typedHeader.mapId || mapId
    endEvent.Indent = headerIndent
    endEvent.FoldIndent = 0
    endEvent.SeniorFold = false
    endEvent.isFold = 0

    list.splice(
      headerIndex + 1,
      Math.max(0, endExclusive - headerIndex - 1),
      ...bodyEvents,
      endEvent,
    )
    return true
  }

  function normalizeSceneLoopBlocks(list: EventBase[], mapId: string): boolean {
    let changed = false
    for (let index = list.length - 1; index >= 0; index -= 1) {
      const event = list[index]
      if (!(event instanceof EventLoop) && event?.Code !== EventID.Loop) continue
      changed = syncLoopBlockAt(list, mapId, index) || changed
    }
    return changed
  }

  function normalizeSceneAudioResourcePaths(list: EventBase[]): boolean {
    const audioRoot = workspace.virtualResourceRoots.audio
    if (!audioRoot) return false
    const baseFolderByCode = new Map<EventID, string>([
      [EventID.StartBGM, '音乐'],
      [EventID.StartSE, '音效'],
      [EventID.StartVoice, '语音'],
      [EventID.StartBGS, '循环音效'],
    ])
    let changed = false
    for (const event of list) {
      const baseFolder = baseFolderByCode.get(event.Code)
      const current = String(event.Parameter?.[0] ?? '').trim()
      if (!baseFolder || !current) continue
      const next = resolveStoredPathFromDisplayPath(audioRoot, 'audio', current, 'engine', baseFolder)
      if (!next || next === current) continue
      event.Parameter[0] = next
      changed = true
    }
    return changed
  }

  function syncTextChoiceBlockAt(
    list: EventBase[],
    mapId: string,
    headerIndex: number,
    rawChoices: string[],
  ): boolean {
    const header = list[headerIndex]
    if (!(header instanceof EventTextDif) && header?.Code !== EventID.TextDif) {
      return false
    }
    const typedHeader = header as EventTextDif
    const normalizedChoices = normalizeTextChoiceValues(rawChoices)
    const headerIndent = Math.max(0, typedHeader.Indent ?? 0)
    const choiceIndent = headerIndent + 1

    typedHeader.TextDifChoice = normalizedChoices
    typedHeader.FoldIndent = typedHeader.Indent

    const endExclusive = getStructureEndExclusive(list, headerIndex)
    const currentBlock = list.slice(headerIndex + 1, endExclusive)
    const currentChoices: EventTextChoice[] = []
    const choiceBodies: EventBase[][] = []
    let currentEnd: EventTextEnd | undefined
    let currentChoiceIndex = -1
    for (const event of currentBlock) {
      if (!event) continue
      if (event.Code === EventID.TextEnd && event.Indent === headerIndent) {
        currentEnd ??= event as EventTextEnd
        currentChoiceIndex = -1
        continue
      }
      if (
        event.Code === EventID.TextChoice
        && (event.Indent === headerIndent || event.Indent === choiceIndent)
      ) {
        currentChoices.push(event as EventTextChoice)
        choiceBodies.push([])
        currentChoiceIndex = choiceBodies.length - 1
        continue
      }
      if (currentChoiceIndex >= 0) {
        choiceBodies[currentChoiceIndex]?.push(event)
      }
    }
    const hasMatchingChoices = currentChoices.length === normalizedChoices.length
      && currentChoices.every((choice, index) => {
        return choice.Indent === choiceIndent
          && choice.ChoicePos === index
          && String(choice.Content ?? '').trim() === normalizedChoices[index]
      })
    const hasMatchingEnd = !!currentEnd && currentEnd.Indent === headerIndent
    const preservedBodyCount = choiceBodies.reduce((total, items) => total + items.length, 0)
    if (hasMatchingChoices && hasMatchingEnd && currentBlock.length === normalizedChoices.length + preservedBodyCount + 1) {
      return false
    }
    const nextBlock: EventBase[] = []

    for (let index = 0; index < normalizedChoices.length; index += 1) {
      const choiceEvent = currentChoices[index] ?? (createEmptyEventByCode(EventID.TextChoice) as EventTextChoice)
      const oldChoiceIndent = Math.max(0, choiceEvent.Indent ?? 0)
      choiceEvent.mapId = typedHeader.mapId || mapId
      choiceEvent.Indent = choiceIndent
      choiceEvent.FoldIndent = 0
      choiceEvent.SeniorFold = false
      choiceEvent.isFold = 0
      choiceEvent.ChoicePos = index
      choiceEvent.Content = normalizedChoices[index]
      nextBlock.push(choiceEvent)
      const bodyEvents = choiceBodies[index] ?? []
      if (oldChoiceIndent === headerIndent) {
        for (const event of bodyEvents) {
          event.Indent = Math.max(0, Math.max(0, event.Indent ?? 0) + 1)
        }
      }
      nextBlock.push(...bodyEvents)
    }

    const endEvent = currentEnd ?? (createEmptyEventByCode(EventID.TextEnd) as EventTextEnd)
    endEvent.mapId = typedHeader.mapId || mapId
    endEvent.Indent = headerIndent
    endEvent.FoldIndent = 0
    endEvent.SeniorFold = false
    endEvent.isFold = 0
    nextBlock.push(endEvent)

    list.splice(headerIndex + 1, Math.max(0, endExclusive - headerIndex - 1), ...nextBlock)
    return true
  }

  function normalizeSceneTextChoiceBlocks(list: EventBase[], mapId: string): boolean {
    let changed = false
    for (let index = 0; index < list.length; index += 1) {
      const event = list[index]
      if (!(event instanceof EventTextDif) && event?.Code !== EventID.TextDif) continue
      const nextChoices = (event as EventTextDif).TextDifChoice.map((item) => String(item ?? ''))
      changed = syncTextChoiceBlockAt(list, mapId, index, nextChoices) || changed
      index = findStructureBlockEndIndex(list, index)
    }
    return changed
  }

  function syncButtonChoiceBlockAt(
    list: EventBase[],
    mapId: string,
    headerIndex: number,
    rawItems: string[],
  ): boolean {
    const header = list[headerIndex]
    if (!(header instanceof EventButtonDif) && header?.Code !== EventID.ButtonDif) {
      return false
    }
    const typedHeader = header as EventButtonDif
    const normalizedItems = normalizeButtonChoiceItems(rawItems)
    const normalizedChoiceStrings = normalizedItems.map((item) => stringifyButtonChoiceItem(item))
    const currentChoiceStrings = (typedHeader.ButtonChoice ?? []).map((item) => String(item ?? ''))
    const headerChanged = JSON.stringify(currentChoiceStrings) !== JSON.stringify(normalizedChoiceStrings)

    typedHeader.ButtonChoice = normalizedChoiceStrings
    typedHeader.FoldIndent = typedHeader.Indent
    const headerIndent = Math.max(0, typedHeader.Indent ?? 0)
    const choiceIndent = headerIndent + 1

    const endExclusive = getStructureEndExclusive(list, headerIndex)
    const currentBlock = list.slice(headerIndex + 1, endExclusive)
    const currentChoices: EventButtonDifChoose[] = []
    const choiceBodies: EventBase[][] = []
    let currentEnd: EventButtonDifEnd | undefined
    let currentChoiceIndex = -1
    for (const event of currentBlock) {
      if (!event) continue
      if (event.Code === EventID.ButtonDifEnd && event.Indent === headerIndent) {
        currentEnd ??= event as EventButtonDifEnd
        currentChoiceIndex = -1
        continue
      }
      if (
        event.Code === EventID.ButtonDifChoose
        && (event.Indent === headerIndent || event.Indent === choiceIndent)
      ) {
        currentChoices.push(event as EventButtonDifChoose)
        choiceBodies.push([])
        currentChoiceIndex = choiceBodies.length - 1
        continue
      }
      if (currentChoiceIndex >= 0) {
        choiceBodies[currentChoiceIndex]?.push(event)
      }
    }
    const hasMatchingChoices = currentChoices.length === normalizedItems.length
      && currentChoices.every((choice, index) => {
        const choiceIndex = Number.parseInt(normalizedItems[index]?.[0] ?? '', 10)
        return choice.Indent === choiceIndent
          && choice.Index === (Number.isFinite(choiceIndex) ? choiceIndex : 0)
      })
    const hasMatchingEnd = !!currentEnd && currentEnd.Indent === headerIndent
    const preservedBodyCount = choiceBodies.reduce((total, items) => total + items.length, 0)
    if (headerChanged === false && hasMatchingChoices && hasMatchingEnd && currentBlock.length === normalizedItems.length + preservedBodyCount + 1) {
      return false
    }

    const nextBlock: EventBase[] = []
    for (let index = 0; index < normalizedItems.length; index += 1) {
      const choiceEvent = currentChoices[index] ?? (createEmptyEventByCode(EventID.ButtonDifChoose) as EventButtonDifChoose)
      const oldChoiceIndent = Math.max(0, choiceEvent.Indent ?? 0)
      const choiceIndex = Number.parseInt(normalizedItems[index]?.[0] ?? '', 10)
      const resolvedChoiceIndex = Number.isFinite(choiceIndex) ? choiceIndex : 0
      const systemButtonName = String(
        workspace.project?.systemData?.btnList?.buttons?.[resolvedChoiceIndex]?.Name ?? '',
      ).trim()
      choiceEvent.mapId = typedHeader.mapId || mapId
      choiceEvent.Indent = choiceIndent
      choiceEvent.FoldIndent = 0
      choiceEvent.SeniorFold = false
      choiceEvent.isFold = 0
      choiceEvent.Index = resolvedChoiceIndex
      choiceEvent.Name = systemButtonName || getDefaultButtonChoiceName(index)
      nextBlock.push(choiceEvent)
      const bodyEvents = choiceBodies[index] ?? []
      if (oldChoiceIndent === headerIndent) {
        for (const event of bodyEvents) {
          event.Indent = Math.max(0, Math.max(0, event.Indent ?? 0) + 1)
        }
      }
      nextBlock.push(...bodyEvents)
    }

    const endEvent = currentEnd ?? (createEmptyEventByCode(EventID.ButtonDifEnd) as EventButtonDifEnd)
    endEvent.mapId = typedHeader.mapId || mapId
    endEvent.Indent = headerIndent
    endEvent.FoldIndent = 0
    endEvent.SeniorFold = false
    endEvent.isFold = 0
    nextBlock.push(endEvent)

    list.splice(headerIndex + 1, Math.max(0, endExclusive - headerIndex - 1), ...nextBlock)
    return true
  }

  function normalizeSceneButtonChoiceBlocks(list: EventBase[], mapId: string): boolean {
    let changed = false
    for (let index = 0; index < list.length; index += 1) {
      const event = list[index]
      if (!(event instanceof EventButtonDif) && event?.Code !== EventID.ButtonDif) continue
      const nextItems = (event as EventButtonDif).ButtonChoice.map((item) => String(item ?? ''))
      changed = syncButtonChoiceBlockAt(list, mapId, index, nextItems) || changed
      index = findStructureBlockEndIndex(list, index)
    }
    return changed
  }

  function syncIfBlockAt(
    list: EventBase[],
    mapId: string,
    headerIndex: number,
    hasElse: boolean,
  ): boolean {
    const header = list[headerIndex]
    if (!(header instanceof EventIF) && header?.Code !== EventID.IF) {
      return false
    }
    const typedHeader = header as EventIF
    typedHeader.IsHaveElse = hasElse
    typedHeader.FoldIndent = typedHeader.Indent
    const headerIndent = Math.max(0, typedHeader.Indent ?? 0)
    let changed = false
    let endIndex = -1
    const choiceIndices: number[] = []

    for (let index = headerIndex + 1; index < list.length; index += 1) {
      const event = list[index]
      if (!event) break
      const indent = Math.max(0, event.Indent ?? 0)
      if (indent < headerIndent) break
      if (indent === headerIndent && event.Code === EventID.IFEnd) {
        if (endIndex === -1) {
          endIndex = index
        } else {
          list.splice(index, 1)
          index -= 1
          changed = true
        }
        continue
      }
      if (indent === headerIndent && event.Code === EventID.IfChoice) {
        choiceIndices.push(index)
        continue
      }
      if (indent === headerIndent && index > headerIndex) {
        break
      }
    }

    if (endIndex === -1) {
      const insertAt = findStructureBlockEndIndex(list, headerIndex) + 1
      const endEvent = createEmptyEventByCode(EventID.IFEnd) as EventIFEnd
      endEvent.mapId = typedHeader.mapId || mapId
      endEvent.Indent = headerIndent
      endEvent.FoldIndent = 0
      endEvent.SeniorFold = false
      endEvent.isFold = 0
      list.splice(insertAt, 0, endEvent)
      endIndex = insertAt
      changed = true
    } else {
      const endEvent = list[endIndex] as EventIFEnd
      if (
        endEvent.mapId !== (typedHeader.mapId || mapId)
        || endEvent.Indent !== headerIndent
        || endEvent.FoldIndent !== 0
        || endEvent.SeniorFold !== false
        || endEvent.isFold !== 0
      ) {
        endEvent.mapId = typedHeader.mapId || mapId
        endEvent.Indent = headerIndent
        endEvent.FoldIndent = 0
        endEvent.SeniorFold = false
        endEvent.isFold = 0
        changed = true
      }
    }

    if (choiceIndices.length > 1) {
      for (let i = choiceIndices.length - 1; i >= 1; i -= 1) {
        const index = choiceIndices[i]!
        list.splice(index, 1)
        if (index < endIndex) endIndex -= 1
        changed = true
      }
      choiceIndices.splice(1)
    }

    if (hasElse) {
      if (!choiceIndices.length) {
        const elseEvent = createEmptyEventByCode(EventID.IfChoice) as EventIfChoice
        elseEvent.mapId = typedHeader.mapId || mapId
        elseEvent.Indent = headerIndent
        elseEvent.FoldIndent = 0
        elseEvent.SeniorFold = false
        elseEvent.isFold = 0
        list.splice(endIndex, 0, elseEvent)
        changed = true
      } else {
        const elseEvent = list[choiceIndices[0]!] as EventIfChoice
        if (
          elseEvent.mapId !== (typedHeader.mapId || mapId)
          || elseEvent.Indent !== headerIndent
          || elseEvent.FoldIndent !== 0
          || elseEvent.SeniorFold !== false
          || elseEvent.isFold !== 0
        ) {
          elseEvent.mapId = typedHeader.mapId || mapId
          elseEvent.Indent = headerIndent
          elseEvent.FoldIndent = 0
          elseEvent.SeniorFold = false
          elseEvent.isFold = 0
          changed = true
        }
      }
    } else if (choiceIndices.length) {
      const elseIndex = choiceIndices[0]!
      const deleteCount = Math.max(1, endIndex - elseIndex)
      list.splice(elseIndex, deleteCount)
      changed = true
    }

    return changed
  }

  function normalizeSceneIfBlocks(list: EventBase[], mapId: string): boolean {
    let changed = false
    for (let index = 0; index < list.length; index += 1) {
      const event = list[index]
      if (!(event instanceof EventIF) && event?.Code !== EventID.IF) continue
      changed = syncIfBlockAt(list, mapId, index, Boolean((event as EventIF).IsHaveElse)) || changed
      index = findStructureBlockEndIndex(list, index)
    }
    return changed
  }

  function completeStructureBlockSync(
    list: EventBase[],
    headerIndex: number,
    label: string,
    changed: boolean,
  ): boolean {
    if (!changed) return false
    syncEventStructureState(list)
    bumpSceneStructureRevision()
    refreshStructureBlockSelectionIfNeeded(list, headerIndex)
    assertCurrentSceneEventReferences(label)
    return true
  }

  function syncTextChoiceBlock(headerIndex: number, rawChoices: string[]): boolean {
    const list = selectedSceneEvents.value
    const map = selectedMap.value
    const mapId = String(map?.Id ?? -1)
    return completeStructureBlockSync(
      list,
      headerIndex,
      'syncTextChoiceBlock',
      syncTextChoiceBlockAt(list, mapId, headerIndex, rawChoices),
    )
  }

  function syncButtonChoiceBlock(headerIndex: number, rawItems: string[]): boolean {
    const list = selectedSceneEvents.value
    const map = selectedMap.value
    const mapId = String(map?.Id ?? -1)
    return completeStructureBlockSync(
      list,
      headerIndex,
      'syncButtonChoiceBlock',
      syncButtonChoiceBlockAt(list, mapId, headerIndex, rawItems),
    )
  }

  function syncIfBlock(headerIndex: number, hasElse: boolean): boolean {
    const list = selectedSceneEvents.value
    const map = selectedMap.value
    const mapId = String(map?.Id ?? -1)
    return completeStructureBlockSync(
      list,
      headerIndex,
      'syncIfBlock',
      syncIfBlockAt(list, mapId, headerIndex, hasElse),
    )
  }

  function syncLoopBlock(headerIndex: number): boolean {
    const list = selectedSceneEvents.value
    const map = selectedMap.value
    const mapId = String(map?.Id ?? -1)
    return completeStructureBlockSync(
      list,
      headerIndex,
      'syncLoopBlock',
      syncLoopBlockAt(list, mapId, headerIndex),
    )
  }

  function updateTextChoiceContent(headerIndex: number, choiceIndex: number, value: string): boolean {
    const list = selectedSceneEvents.value
    const header = list[headerIndex]
    if (!(header instanceof EventTextDif) && header?.Code !== EventID.TextDif) {
      return false
    }

    const typedHeader = header as EventTextDif
    const nextChoices = typedHeader.TextDifChoice.map((item) => String(item ?? ''))
    while (nextChoices.length <= choiceIndex) {
      nextChoices.push(getDefaultTextChoiceLabel(nextChoices.length))
    }
    nextChoices[choiceIndex] = value
    typedHeader.TextDifChoice = nextChoices

    const choiceIndent = Math.max(0, typedHeader.Indent) + 1
    const endExclusive = findStructureBlockEndIndex(list, headerIndex) + 1
    let optionCursor = 0
    for (let index = headerIndex + 1; index < endExclusive; index += 1) {
      const event = list[index]
      if (!event || event.Code !== EventID.TextChoice || event.Indent !== choiceIndent) continue
      if (optionCursor === choiceIndex) {
        const choiceEvent = event as EventTextChoice
        choiceEvent.ChoicePos = choiceIndex
        choiceEvent.Content = value
        touchSceneEvents()
        return true
      }
      optionCursor += 1
    }

    return syncTextChoiceBlock(headerIndex, nextChoices)
  }

  function buildEventRangeSet(start: number, end: number): Set<number> {
    const next = new Set<number>()
    if (start < 0 || end < start) return next
    for (let i = start; i <= end; i += 1) next.add(i)
    return next
  }

  /** 当前选中集是否与主选中行单次点选范围一致。 */
  function selectionMatchesPrimaryClickRange(
    list: EventBase[],
    primary: number,
    indices: Set<number>,
  ): boolean {
    if (indices.size <= 1) return true
    if (primary < 0 || primary >= list.length) return false
    const range = getEventSelectionRange(list, primary)
    const expected = buildEventRangeSet(range.start, range.end)
    if (expected.size !== indices.size) return false
    for (const index of expected) {
      if (!indices.has(index)) return false
    }
    return true
  }

  /**
   * 是否可展示当前选中事件的属性面板。
   * Ctrl/Shift/行号拖选等多选时不展示；一次点选整块结构时仍展示主事件属性。
   */
  const canShowSelectedEventProperty = computed(() => {
    const event = selectedEvent.value
    const primary = selectedEventIndex.value
    if (!event || primary === null) return false
    return selectionMatchesPrimaryClickRange(
      selectedEventList.value,
      primary,
      selectedEventIndices.value,
    )
  })

  /**
   * 事件属性区空态文案：多选歧义时提示数量，否则返回调用方提供的默认文案。
   * @param fallback 未选中或单选可展示属性时的提示语。
   */
  function formatEventPropertyEmptyHint(fallback: string): string {
    const indices = selectedEventIndices.value
    if (indices.size > 1 && !canShowSelectedEventProperty.value) {
      return `已选中 ${indices.size} 个事件`
    }
    return fallback
  }

  function normalizeSelectionSet(indices: Iterable<number>, listLength: number): Set<number> {
    const next = new Set<number>()
    for (const index of indices) {
      if (index >= 0 && index < listLength) next.add(index)
    }
    return next
  }

  function applySelectedEventIndices(next: Set<number>, primaryIndex: number | null, anchorIndex: number | null) {
    selectedEventIndices.value = next
    selectedEventIndex.value = primaryIndex
    selectedEvent.value = primaryIndex === null ? null : (selectedEventList.value[primaryIndex] ?? null)
    selectedEventAnchorIndex.value = anchorIndex
    selectedAddPlaceholder.value = null
  }

  /* ——— 展开祖先节点 ——— */
  function expandSceneAncestors(sceneId: number | null) {
    if (sceneId === null) return
    const parentMap = storyParentMap.value
    const nextExpanded = new Set(expandedSceneKeys.value)
    let cursor = sceneId
    let guard = 0
    while (guard < 2000) {
      nextExpanded.add(cursor)
      const parentId = parentMap.get(cursor)
      if (!parentId || parentId <= 0 || parentId === cursor) break
      cursor = parentId
      guard += 1
    }
    expandedSceneKeys.value = [...nextExpanded]
  }

  /* ——— 选中场景 ——— */
  function selectScene(id: number) {
    const st = getStoryTree()
    if (st) {
      st.SelectMapIndex = id
    }
    selectedCustomUi.value = null
    selectedSceneId.value = id
  }

  function selectEventAtIndex(index: number | null) {
    const list = selectedEventList.value
    if (index === null || index < 0 || index >= list.length) {
      applySelectedEventIndices(new Set(), null, null)
      return
    }
    const range = getEventSelectionRange(list, index)
    applySelectedEventIndices(buildEventRangeSet(range.start, range.end), index, index)
  }

  /**
   * 结构块行数变化后，若当前选中仍落在此块内，则按新范围重算选中集。
   */
  function refreshStructureBlockSelectionIfNeeded(list: EventBase[], headerIndex: number): void {
    const primary = selectedEventIndex.value
    const selected = selectedEventIndices.value
    if (primary === null && selected.size === 0) return
    let shouldRefresh = primary === headerIndex || selected.has(headerIndex)
    if (!shouldRefresh && primary !== null) {
      const primaryContext = getStructureSelectionContext(list, primary)
      shouldRefresh = primaryContext.kind !== 'none' && primaryContext.headerIndex === headerIndex
    }
    if (!shouldRefresh) {
      for (const index of selected) {
        const context = getStructureSelectionContext(list, index)
        if (context.kind !== 'none' && context.headerIndex === headerIndex) {
          shouldRefresh = true
          break
        }
      }
    }
    if (!shouldRefresh) return
    selectEventAtIndex(headerIndex)
  }

  function selectEventWithModifiers(
    index: number,
    options: { append?: boolean; range?: boolean } = {},
  ) {
    const list = selectedEventList.value
    if (index < 0 || index >= list.length) return
    const range = getEventSelectionRange(list, index)
    const clicked = buildEventRangeSet(range.start, range.end)
    if (options.range) {
      const anchor = selectedEventAnchorIndex.value ?? selectedEventIndex.value ?? range.start
      const anchorRange = getEventSelectionRange(list, anchor)
      const start = Math.min(anchorRange.start, range.start)
      const end = Math.max(anchorRange.end, range.end)
      const expanded = expandStructureBlocksInIndexRange(list, start, end)
      applySelectedEventIndices(buildEventRangeSet(expanded.start, expanded.end), index, anchor)
      return
    }
    if (options.append) {
      const next = new Set(selectedEventIndices.value)
      const clickedCode = list[index]?.Code ?? -1
      const shouldRemove = !isStructureHeaderCode(clickedCode)
        && !isStructureReadonlyCode(clickedCode)
        && [...clicked].every((item) => next.has(item))
      for (const item of clicked) {
        if (shouldRemove) next.delete(item)
        else next.add(item)
      }
      const normalized = normalizeSelectionSet(next, list.length)
      const fallback = normalized.size ? Math.min(...normalized) : null
      applySelectedEventIndices(normalized, normalized.has(index) ? index : fallback, index)
      return
    }
    applySelectedEventIndices(clicked, index, index)
  }

  function selectAddPlaceholder(payload: AddPlaceholderSelection | null) {
    selectedAddPlaceholder.value = payload
    selectedEventIndices.value = new Set()
    selectedEventIndex.value = null
    selectedEvent.value = null
    selectedEventAnchorIndex.value = null
  }

  /* ——— 切换事件折叠 ——— */
  function toggleEventFold(index: number) {
    const list = selectedEventList.value
    const event = list[index]
    if (!event || isStructureReadonlyCode(event.Code ?? -1)) return
    const endIndex = findFoldableEndIndex(list, index)
    if (endIndex <= index) return
    event.isFold = Number(event.isFold) > 0 ? 0 : 1
    syncEventStructureState(list)
    bumpSceneStructureRevision()
    workspace.markProjectDirty()
    if (!selectedCustomUi.value) assertCurrentSceneEventReferences('toggleEventFold')
    selectEventAtIndex(index)
  }

  function cloneEventForPaste(event: unknown): EventBase {
    return ensureEventInstance(JSON.parse(JSON.stringify(event)) as EventBase)
  }

  function buildClipboardInsertingEvents(
    context: ActiveEventEditContext,
    clipboard: NonNullable<typeof eventClipboard.value>,
    cloneForCopy: boolean,
  ): EventBase[] {
    if (cloneForCopy && clipboard.mode === 'copy') {
      return clipboard.events.map((event) => {
        const cloned = cloneEventForPaste(event)
        cloned.mapId = context.map ? String(context.map.Id) : ''
        return cloned
      })
    }
    const events = clipboard.events
      .map((event) => ensureEventInstance(event as EventBase))
      .filter(Boolean)
    for (const event of events) event.mapId = context.map ? String(context.map.Id) : ''
    return events
  }

  function validatePasteInsertPlan(
    afterIndex: number | null,
    insertingEvents: EventBase[],
  ): ValidateEventInsertResult {
    const context = getActiveEventEditContext()
    if (!context || !insertingEvents.length) return { ok: false, reason: 'empty' }
    const placement = resolveActiveInsertPlacement(context.list, afterIndex)
    return validateEventInsert({
      targetList: context.list,
      placement,
      insertingEvents,
      selectionIndex: afterIndex,
      allowReadonlyAnchor: !!selectedAddPlaceholder.value,
    })
  }

  function commitEventBlockInsert(
    targetList: EventBase[],
    insertingEvents: EventBase[],
    placement: InsertPlacement,
    nextIndent: number,
    selectionIndex: number | null,
  ): { insertAt: number; events: EventBase[] } | null {
    const result = insertEventsAtPlacement(
      targetList,
      insertingEvents,
      placement,
      nextIndent,
      { selectionIndex },
    )
    if (!result) return null
    syncEventStructureState(targetList)
    bumpSceneStructureRevision()
    applySelectedEventIndices(
      buildEventRangeSet(result.insertAt, result.insertAt + result.events.length - 1),
      result.insertAt,
      result.insertAt,
    )
    return result
  }

  function applyEventBlockInsert(
    targetList: EventBase[],
    insertingEvents: EventBase[],
    placement: InsertPlacement,
    selectionIndex: number | null,
  ): { insertAt: number; events: EventBase[] } | null {
    const validated = validateEventInsert({
      targetList,
      placement,
      insertingEvents,
      selectionIndex,
      allowReadonlyAnchor: !!selectedAddPlaceholder.value,
    })
    if (!validated.ok) return null
    return commitEventBlockInsert(
      targetList,
      insertingEvents,
      placement,
      validated.nextIndent,
      selectionIndex,
    )
  }

  function insertEventBlock(
    events: EventBase[],
    afterIndex: number | null = selectedEventIndex.value,
    options?: { insertAfterChoiceBlock?: boolean },
  ): EventBase[] | null {
    const context = getActiveEventEditContext()
    if (!context || !events.length) return null
    const placement = options?.insertAfterChoiceBlock
      ? resolveInsertPlacementAfterIndex(
        context.list,
        resolveChoiceBlockInsertAfterIndex(context.list, afterIndex),
      )
      : resolveActiveInsertPlacement(context.list, afterIndex)
    const insertingEvents = events.map((event) => {
      const instance = ensureEventInstance(event)
      instance.mapId = context.map ? String(context.map.Id) : ''
      return instance
    })
    const inserted = applyEventBlockInsert(context.list, insertingEvents, placement, afterIndex)
    if (!inserted) return null
    if (!context.isCustomUi) assertCurrentSceneEventReferences('insertEventBlock')
    return inserted.events
  }

  /**
   * 复制/剪切/粘贴/删除/拖拽共用的选中下标列表；与列表高亮 selectedEventIndices 一致。
   * 若选中集意外为空但仍有主选中行，会按主行点选范围补全并回写选中集。
   */
  function getSelectedEventIndicesForOperation(): number[] {
    const list = selectedEventList.value
    const primary = selectedEventIndex.value
    const indices = resolveSelectedEventIndices(list, selectedEventIndices.value, primary)
    if (!selectedEventIndices.value.size && indices.length && primary !== null) {
      applySelectedEventIndices(
        new Set(indices),
        primary,
        selectedEventAnchorIndex.value ?? primary,
      )
    }
    return indices
  }

  function deleteSelectedEvents(): boolean {
    const context = getActiveEventEditContext()
    if (!context) return false
    const list = context.list
    const indices = getSelectedEventIndicesForOperation()
    if (!indices.length) return false
    if (hasBlockedReadonlyStructureInSelection(list, indices)) return false
    const beforeSelection = captureHistorySelection()
    const beforeList = cloneHistoryEvents(list)
    const beforeRanges = captureEventHistoryRanges(list, indices)
    const items = captureEventHistoryItems(list, indices)
    for (let i = indices.length - 1; i >= 0; i -= 1) {
      list.splice(indices[i]!, 1)
    }
    const nextIndex = resolveSelectionAfterRemove(indices, list.length)
    touchActiveEventList(context)
    selectEventAtIndex(nextIndex)
    history.push({
      domain: 'event',
      action: 'delete',
      label: '删除事件',
      beforeSelection,
      afterSelection: captureHistorySelection(),
      mapId: context.historyMapId,
      items,
      beforeList,
      afterList: cloneHistoryEvents(list),
      beforeRanges,
      afterRanges: beforeRanges.map((range) => ({ start: range.start, events: [] })),
    })
    return true
  }

  function copySelectedEvents(): boolean {
    const indices = getSelectedEventIndicesForOperation()
    const list = selectedEventList.value
    if (!indices.length) return false
    if (hasBlockedReadonlyStructureInSelection(list, indices)) return false
    eventClipboard.value = {
      mode: 'copy',
      sourceMapId: selectedCustomUi.value ? null : selectedSceneId.value,
      events: indices.map((index) => list[index]!).filter(Boolean),
    }
    return true
  }

  function cutSelectedEvents(): boolean {
    const context = getActiveEventEditContext()
    if (!context) return false
    const indices = getSelectedEventIndicesForOperation()
    const list = context.list
    if (!indices.length) return false
    if (hasBlockedReadonlyStructureInSelection(list, indices)) return false
    const beforeSelection = captureHistorySelection()
    const beforeList = cloneHistoryEvents(list)
    const beforeRanges = captureEventHistoryRanges(list, indices)
    const items = captureEventHistoryItems(list, indices)
    const cutEvents = indices.map((index) => list[index]!).filter(Boolean)
    if (!cutEvents.length) return false
    eventClipboard.value = {
      mode: 'cut',
      sourceMapId: context.map?.Id ?? null,
      events: cutEvents,
    }
    for (let i = indices.length - 1; i >= 0; i -= 1) {
      list.splice(indices[i]!, 1)
    }
    const nextIndex = resolveSelectionAfterRemove(indices, list.length)
    touchActiveEventList(context)
    selectEventAtIndex(nextIndex)
    history.push({
      domain: 'event',
      action: 'delete',
      label: '剪切事件',
      beforeSelection,
      afterSelection: captureHistorySelection(),
      mapId: context.historyMapId,
      items,
      beforeList,
      afterList: cloneHistoryEvents(list),
      beforeRanges,
      afterRanges: beforeRanges.map((range) => ({ start: range.start, events: [] })),
    })
    return true
  }

  function clearEventClipboard() {
    eventClipboard.value = null
  }

  /**
   * 当前选中位置的粘贴校验结果（供菜单置灰与失败提示）。
   */
  function validatePasteAtSelection(
    afterIndex: number | null = selectedEventIndex.value,
  ): ValidateEventInsertResult {
    const clipboard = eventClipboard.value
    const context = getActiveEventEditContext()
    if (!clipboard?.events.length || !context) return { ok: false, reason: 'empty' }
    const insertingEvents = buildClipboardInsertingEvents(context, clipboard, false)
    return validatePasteInsertPlan(afterIndex, insertingEvents)
  }

  function canPasteAtSelection(): boolean {
    return validatePasteAtSelection().ok
  }

  /**
   * 拖拽移动预检：目标行与方向是否允许当前选中集落点。
   */
  function canMoveSelectedEventsTo(targetIndex: number, position: 'before' | 'after'): boolean {
    return canMoveEventsAt(
      selectedEventList.value,
      getSelectedEventIndicesForOperation(),
      targetIndex,
      position,
    )
  }

  function pasteClipboardEvents(afterIndex: number | null = selectedEventIndex.value): boolean {
    const clipboard = eventClipboard.value
    const context = getActiveEventEditContext()
    if (!clipboard || !clipboard.events.length || !context) return false
    const beforeSelection = captureHistorySelection()
    const targetList = context.list
    const beforeList = cloneHistoryEvents(targetList)
    const insertingEvents = buildClipboardInsertingEvents(context, clipboard, true)
    if (!insertingEvents.length) {
      if (clipboard.mode === 'cut') clearEventClipboard()
      return false
    }
    const validated = validatePasteInsertPlan(afterIndex, insertingEvents)
    if (!validated.ok) return false
    const placement = resolveActiveInsertPlacement(targetList, afterIndex)
    const inserted = commitEventBlockInsert(
      targetList,
      insertingEvents,
      placement,
      validated.nextIndent,
      afterIndex,
    )
    if (!inserted) return false
    if (clipboard.mode === 'cut') clearEventClipboard()
    if (!context.isCustomUi) assertCurrentSceneEventReferences('pasteClipboardEvents')
    history.push({
      domain: 'event',
      action: 'create',
      label: '粘贴事件',
      beforeSelection,
      afterSelection: captureHistorySelection(),
      mapId: context.historyMapId,
      index: inserted.insertAt,
      events: captureEventHistoryRange(targetList, inserted.insertAt, inserted.events.length),
      beforeList,
      afterList: cloneHistoryEvents(targetList),
      beforeRanges: [{ start: inserted.insertAt, events: [] }],
      afterRanges: [captureEventHistoryRangeSnapshot(targetList, inserted.insertAt, inserted.events.length)],
    })
    return true
  }

  function moveSelectedEvents(targetIndex: number, position: 'before' | 'after'): boolean {
    const context = getActiveEventEditContext()
    if (!context) return false
    const list = context.list
    const indices = getSelectedEventIndicesForOperation()
    if (!indices.length) return false
    const beforeSelection = captureHistorySelection()
    const beforeList = cloneHistoryEvents(list)
    const beforeRanges = captureEventHistoryRanges(list, indices)
    const fromItems = captureEventHistoryItems(list, indices)
    const insertBase = position === 'after' ? targetIndex + 1 : targetIndex
    if (indices.includes(targetIndex) || indices.includes(insertBase)) return false
    const moved = indices.map((index) => list[index]!).filter(Boolean)
    const targetEvent = list[targetIndex]
    if (!targetEvent) return false
    const movingSet = new Set(indices)
    const remaining = list.filter((_, index) => !movingSet.has(index))
    const targetIndexInRemaining = remaining.indexOf(targetEvent)
    if (targetIndexInRemaining < 0) return false
    const placement = resolveInsertPlacementFromDrop(remaining, targetIndexInRemaining, position)
    const validated = validateEventInsert({
      targetList: remaining,
      placement,
      insertingEvents: moved,
      dropTarget: { targetIndex: targetIndexInRemaining, position },
    })
    if (!validated.ok) return false
    for (let i = indices.length - 1; i >= 0; i -= 1) {
      list.splice(indices[i]!, 1)
    }
    const inserted = insertEventsAtPlacement(list, moved, placement, validated.nextIndent)
    if (!inserted) return false
    const insertAt = inserted.insertAt
    touchActiveEventList(context)
    applySelectedEventIndices(
      buildEventRangeSet(insertAt, insertAt + moved.length - 1),
      insertAt,
      insertAt,
    )
    history.push({
      domain: 'event',
      action: 'move',
      label: '移动事件',
      beforeSelection,
      afterSelection: captureHistorySelection(),
      mapId: context.historyMapId,
      fromItems,
      toIndex: insertAt,
      afterEvents: captureEventHistoryRange(list, insertAt, moved.length),
      beforeList,
      afterList: cloneHistoryEvents(list),
      beforeRanges,
      afterRanges: [captureEventHistoryRangeSnapshot(list, insertAt, moved.length)],
    })
    return true
  }

  /* ——— 剧情树操作 ——— */

  /** 在指定剧情后插入同级新剧情；未传则追加到根层末尾。 */
  function addStoryMap(afterId: number | null = selectedSceneId.value): MapInfo | null {
    const st = getStoryTree()
    if (!st) return null
    const beforeSelection = captureHistorySelection()
    const nm = addStoryMapSiblingAfter(st, afterId)
    storyStructureRevision.value++
    selectedSceneId.value = nm.Id
    expandSceneAncestors(nm.Id)
    history.push({
      domain: 'story',
      action: 'create',
      label: '新增剧情',
      beforeSelection,
      afterSelection: captureHistorySelection(),
      maps: [captureMapHistoryBundle(st, nm)],
    })
    workspace.markProjectDirty()
    return nm
  }

  /** 在指定剧情下插入子剧情。 */
  function addChildStoryMap(parentId: number = selectedSceneId.value ?? 0): MapInfo | null {
    const st = getStoryTree()
    if (!st || !parentId) return null
    const beforeSelection = captureHistorySelection()
    const nm = addStoryMapChild(st, parentId)
    if (!nm) return null
    storyStructureRevision.value++
    selectedSceneId.value = nm.Id
    expandSceneAncestors(nm.Id)
    history.push({
      domain: 'story',
      action: 'create',
      label: '新增子剧情',
      beforeSelection,
      afterSelection: captureHistorySelection(),
      maps: [captureMapHistoryBundle(st, nm)],
    })
    workspace.markProjectDirty()
    return nm
  }

  /** 修改剧情名称。 */
  function renameScene(id: number, name: string): boolean {
    const st = getStoryTree()
    if (!st) return false
    const beforeMap = st.Maps.find((map) => map.Id === id)
    if (!beforeMap) return false
    const beforeSelection = captureHistorySelection()
    const beforeMaps = [captureMapHistorySnapshot(beforeMap)]
    const ok = renameStoryMap(st, id, name)
    if (!ok) return false
    storyStructureRevision.value++
    const afterMap = st.Maps.find((map) => map.Id === id)
    if (afterMap) {
      history.push({
        domain: 'story',
        action: 'update',
        label: '修改剧情名称',
        beforeSelection,
        afterSelection: captureHistorySelection(),
        beforeMaps,
        afterMaps: [captureMapHistorySnapshot(afterMap)],
      })
    }
    workspace.markProjectDirty()
    return true
  }

  /** 删除指定剧情及子剧情后是否仍至少保留一条剧情。 */
  function canDeleteStoryMap(id: number | null = selectedSceneId.value): boolean {
    const st = getStoryTree()
    if (!st || id === null) return false
    return canDeleteMapCascade(st, id)
  }

  /** 删除指定剧情及子剧情，返回是否成功 */
  function deleteCurrentStoryMap(id: number | null = selectedSceneId.value): boolean {
    const st = getStoryTree()
    if (!st || id === null) return false
    const beforeSelection = captureHistorySelection()
    const maps = captureStorySubtreeBundles(id)
    if (!deleteMapCascade(st, id)) return false
    bumpSceneStructureRevision()
    storyStructureRevision.value++
    if (selectedSceneId.value === id) {
      const list = storyTreeList.value
      selectedSceneId.value = list[0]?.Id ?? null
    }
    history.push({
      domain: 'story',
      action: 'delete',
      label: '删除剧情',
      beforeSelection,
      afterSelection: captureHistorySelection(),
      maps,
    })
    workspace.markProjectDirty()
    return true
  }

  /** 复制指定剧情及其子剧情到剧情剪贴板。 */
  function copyStoryMap(id: number | null = selectedSceneId.value): boolean {
    if (id === null) return false
    const maps = captureStoryClipboardMaps(id)
    if (!maps.length) return false
    storyClipboard.value = { mode: 'copy', maps }
    return true
  }

  /** 剪切指定剧情及其子剧情，至少保留一个剧情。 */
  function cutStoryMap(id: number | null = selectedSceneId.value): boolean {
    if (id === null) return false
    const maps = captureStoryClipboardMaps(id)
    const st = getStoryTree()
    if (!st || !maps.length || !canDeleteMapCascade(st, id)) return false
    storyClipboard.value = { mode: 'cut', maps }
    return deleteCurrentStoryMap(id)
  }

  /** 粘贴剧情剪贴板到指定父剧情下；未指定父剧情时粘贴到根层。 */
  function pasteStoryMap(parentId: number | null = selectedSceneId.value): boolean {
    const st = getStoryTree()
    const clipboard = storyClipboard.value
    if (!st || !clipboard?.maps.length) return false
    const beforeSelection = captureHistorySelection()
    const idMap = new Map<number, number>()
    let nextId = Math.max(0, ...st.Maps.map((map) => map.Id)) + 1
    for (const item of clipboard.maps) {
      idMap.set(item.sourceId, nextId)
      nextId += 1
    }
    const targetParentId = parentId && st.Maps.some((map) => map.Id === parentId) ? parentId : 0
    const siblings = st.Maps.filter((map) => map.ParentId === targetParentId)
    const insertedMaps: MapInfo[] = []
    for (const item of clipboard.maps) {
      const newId = idMap.get(item.sourceId)
      if (!newId) continue
      const mappedParentId = idMap.get(item.sourceParentId)
      const nextParentId = item.sourceId === clipboard.maps[0]?.sourceId
        ? targetParentId
        : mappedParentId ?? targetParentId
      const map = new MapInfo({
        name: item.name,
        id: newId,
        parentId: nextParentId,
        order: nextParentId === targetParentId ? siblings.length + insertedMaps.length : item.order,
        list: [],
      })
      map.IsHard = item.isHard
      const events = item.events.map((event) => {
        const cloned = cloneEventForPaste(event)
        cloned.mapId = String(newId)
        return cloned
      })
      map.List = events
      st.Maps.push(map)
      st.Events[String(newId)] = events
      insertedMaps.push(map)
    }
    if (!insertedMaps.length) return false
    st.createStoryTree()
    storyStructureRevision.value++
    bumpSceneStructureRevision()
    selectedSceneId.value = insertedMaps[0]!.Id
    expandSceneAncestors(insertedMaps[0]!.Id)
    if (clipboard.mode === 'cut') storyClipboard.value = null
    history.push({
      domain: 'story',
      action: 'create',
      label: '粘贴剧情',
      beforeSelection,
      afterSelection: captureHistorySelection(),
      maps: insertedMaps.map((map) => captureMapHistoryBundle(st, map)),
    })
    workspace.markProjectDirty()
    return true
  }

  /** 拖拽放置剧情节点 */
  function handleStoryDrop(dragId: number, targetId: number, dropPosition: 'before' | 'inside' | 'after'): boolean {
    const st = getStoryTree()
    if (!st) return false
    const affectedIds = getStoryMoveAffectedMapIds(dragId, targetId, dropPosition)
    const beforeSelection = captureHistorySelection()
    const beforeMaps = captureStoryMapSnapshots(affectedIds)
    const ok = applyStoryMapDrop(st, dragId, targetId, dropPosition)
    if (ok) {
      storyStructureRevision.value++
      history.push({
        domain: 'story',
        action: 'move',
        label: '移动剧情',
        beforeSelection,
        afterSelection: captureHistorySelection(),
        beforeMaps,
        afterMaps: captureStoryMapSnapshots(affectedIds),
      })
      workspace.markProjectDirty()
    }
    return ok
  }

  /** 判断拖拽放置是否被允许 */
  function allowStoryDrop(dragId: number | null, targetId: number, dropPosition: 'before' | 'inside' | 'after'): boolean {
    if (dragId == null) return true
    if (!Number.isFinite(targetId)) return false
    if (dragId === targetId) return false
    const st = getStoryTree()
    if (!st) return false
    if (dropPosition === 'inside' && isStrictDescendantOf(st, dragId, targetId)) return false
    return true
  }

  /** 在选中位置插入一条新事件 */
  function insertEventFromPalette(
    item: EventPaletteItem,
    options?: { insertAfterChoiceBlock?: boolean },
  ): EventBase | null {
    const context = getActiveEventEditContext()
    if (!context) return null
    const beforeSelection = captureHistorySelection()
    const beforeList = cloneHistoryEvents(context.list)

    let ev: EventBase
    try {
      ev = createEmptyEventByCode(item.eventCode)
    } catch {
      return null
    }

    if (ev instanceof EventText) {
      ev.RoleColor = resolveDialogRoleColor('', project.value?.systemData?.totalSetting?.FontColor_MSG)
    }

    if (item.eventCode === EventID.CallSubStory && ev instanceof EventCallSubStory && context.map) {
      ev.MapId = Math.max(1, Math.round(Number(context.map.Id) || 1))
    }

    if (item.eventCode === EventID.JumpStory && ev instanceof EventJumpStory && context.map) {
      ev.MapId = Math.max(1, Math.round(Number(context.map.Id) || 1))
    }

    if (item.eventCode === EventID.PicShow && ev instanceof EventPicShow) {
      setPicShowPreset(ev, item.picShowPreset)
      switch (item.picShowPreset) {
        case 'far':
          ev.PicId = 1
          break
        case 'background':
          ev.PicId = 2
          break
        case 'foreground':
          ev.PicId = 23
          break
        case 'portrait':
          ev.PicId = 3
          break
        default:
          ev.PicId = 3
          break
      }
    }

    ev.mapId = context.map ? String(context.map.Id) : ''
    const block = item.eventCode === EventID.Loop
      ? [ev, createEmptyEventByCode(EventID.LoopAboveStart)]
      : item.eventCode === EventID.IF
        ? [ev, createEmptyEventByCode(EventID.IFEnd)]
        : [ev]
    const inserted = insertEventBlock(block, selectedEventIndex.value, options)
    if (!inserted?.length) return null
    const list = context.list
    const idx = list.indexOf(inserted[0]!)
    if (idx < 0) return null
    if (item.eventCode === EventID.TextDif) {
      syncTextChoiceBlock(idx, [getDefaultTextChoiceLabel(0), getDefaultTextChoiceLabel(1)])
    }
    if (item.eventCode === EventID.ButtonDif) {
      syncButtonChoiceBlock(idx, [
        stringifyButtonChoiceItem(getDefaultButtonChoiceItem(0)),
        stringifyButtonChoiceItem(getDefaultButtonChoiceItem(1)),
      ])
    }
    selectEventAtIndex(idx)
    if (!context.isCustomUi) assertCurrentSceneEventReferences('insertEventFromPalette')
    const insertedRange = getEventSelectionRange(list, idx)
    history.push({
      domain: 'event',
      action: 'create',
      label: '新增事件',
      beforeSelection,
      afterSelection: captureHistorySelection(),
      mapId: context.historyMapId,
      index: insertedRange.start,
      events: captureEventHistoryRange(list, insertedRange.start, insertedRange.end - insertedRange.start + 1),
      beforeList,
      afterList: cloneHistoryEvents(list),
      beforeRanges: [{ start: insertedRange.start, events: [] }],
      afterRanges: [captureEventHistoryRangeSnapshot(list, insertedRange.start, insertedRange.end - insertedRange.start + 1)],
    })
    return inserted[0]!
  }

  /** 在当前选中场景中插入一条已构造好的事件对象。 */
  function insertEventObject(event: EventBase): EventBase | null {
    return insertEventObjects([event])?.[0] ?? null
  }

  /** 在当前选中场景中一次插入多条已构造好的事件对象。 */
  function insertEventObjects(events: EventBase[], label = '新增事件'): EventBase[] | null {
    if (!events.length) return null
    const context = getActiveEventEditContext()
    if (!context) return null
    const beforeSelection = captureHistorySelection()
    const beforeList = cloneHistoryEvents(context.list)
    const inserted = insertEventBlock(events, selectedEventIndex.value)
    if (!inserted?.length) return null
    if (!context.isCustomUi) assertCurrentSceneEventReferences('insertEventObjects')
    const list = context.list
    const index = list.indexOf(inserted[0]!)
    if (index >= 0) {
      history.push({
        domain: 'event',
        action: 'create',
        label,
        beforeSelection,
        afterSelection: captureHistorySelection(),
        mapId: context.historyMapId,
        index,
        events: captureEventHistoryRange(list, index, inserted.length),
        beforeList,
        afterList: cloneHistoryEvents(list),
        beforeRanges: [{ start: index, events: [] }],
        afterRanges: [captureEventHistoryRangeSnapshot(list, index, inserted.length)],
      })
    }
    return inserted
  }

  /**
   * 标记事件列表结构发生变化。
   * 仅用于增删、移动、折叠和分支块同步；普通属性变化使用 `notifySceneEventValueChanged()`。
   */
  function touchSceneEvents() {
    syncEventStructureState(selectedEventList.value)
    bumpSceneStructureRevision()
    workspace.markProjectDirty()
  }

  /**
   * 将文本导入结果批量写入当前剧情和新建剧情。
   * @param rows 初级文本导入结果。
   * @returns 写入事件和新建剧情统计；当前没有选中剧情时返回 null。
   */
  function importBasicTextRows(rows: BasicTextImportRow[]): TextImportApplyResult | null {
    const st = getStoryTree()
    const map = selectedMap.value
    const proj = project.value
    const fontColor = String(proj?.systemData?.totalSetting?.FontColor_MSG ?? '')
    const roleNameList = proj?.userRm?.NameList
    if (!st || !map || !rows.length) return null
    if (!roleNameList) {
      console.error('[text-import] userRm.NameList 不存在，无法同步角色列表')
    }
    const result = applyBasicTextImport({
      storyTree: st,
      currentMap: map as MapInfo,
      fontColor,
      roleNameList: roleNameList ?? [],
    }, rows)
    storyStructureRevision.value++
    bumpSceneStructureRevision()
    expandSceneAncestors(selectedSceneId.value)
    workspace.markProjectDirty()
    return result
  }

  /**
   * 将高级文本导入结果批量写入当前剧情和新建剧情。
   * @param rows 高级文本导入结果。
   * @returns 写入事件和新建剧情统计；当前没有选中剧情时返回 null。
   */
  function importAdvancedTextRows(rows: AdvancedTextImportRow[]): TextImportApplyResult | null {
    const st = getStoryTree()
    const map = selectedMap.value
    const proj = project.value
    const fontColor = String(proj?.systemData?.totalSetting?.FontColor_MSG ?? '')
    const roleNameList = proj?.userRm?.NameList
    if (!st || !map || !rows.length) return null
    if (!roleNameList) {
      console.error('[text-import] userRm.NameList 不存在，无法同步角色列表')
    }
    const result = applyAdvancedTextImport({
      storyTree: st,
      currentMap: map as MapInfo,
      fontColor,
      roleNameList: roleNameList ?? [],
    }, rows)
    storyStructureRevision.value++
    bumpSceneStructureRevision()
    expandSceneAncestors(selectedSceneId.value)
    workspace.markProjectDirty()
    return result
  }

  function beginEventPropertyHistory() {
    if (history.isApplying || pendingEventHistory) return
    const context = getActiveEventEditContext()
    const index = selectedEventIndex.value
    if (!context || index === null) return
    const event = context.list[index]
    if (!event) return
    const range = getEventSelectionRange(context.list, index)
    pendingEventHistory = {
      mapId: context.historyMapId,
      index,
      beforeEvent: cloneHistoryEvents([event])[0],
      beforeList: cloneHistoryEvents(context.list),
      beforeRanges: [captureEventHistoryRangeSnapshot(context.list, range.start, range.end - range.start + 1)],
      beforeSelection: captureHistorySelection(),
    }
  }

  function commitEventPropertyHistory() {
    if (!pendingEventHistory) return
    const pending = pendingEventHistory
    const st = getStoryTree()
    if (!st) {
      cancelPendingEventHistory()
      return
    }
    const list = resolveHistoryEventList(pending.mapId) ?? st.loadEvent(pending.mapId)
    const event = list[pending.index] ?? null
    cancelPendingEventHistory()
    if (!event) return
    const range = getEventSelectionRange(list, pending.index)
    history.push({
      domain: 'event',
      action: 'update',
      label: '修改事件属性',
      beforeSelection: pending.beforeSelection,
      afterSelection: captureHistorySelection(),
      mapId: pending.mapId,
      index: pending.index,
      beforeEvent: pending.beforeEvent,
      afterEvent: cloneHistoryEvents([event])[0],
      beforeList: pending.beforeList,
      afterList: cloneHistoryEvents(list),
      beforeRanges: pending.beforeRanges,
      afterRanges: [captureEventHistoryRangeSnapshot(list, range.start, range.end - range.start + 1)],
    })
  }

  /**
   * 标记当前事件属性值发生变化。
   * 只刷新事件文案、预览和旧组件兼容修订号，不重建事件结构状态。
   */
  function notifySceneEventValueChanged() {
    beginEventPropertyHistory()
    bumpSceneValueRevision()
    workspace.markProjectDirty()
    if (pendingEventHistoryTimer !== null) {
      window.clearTimeout(pendingEventHistoryTimer)
    }
    pendingEventHistoryTimer = window.setTimeout(() => {
      commitEventPropertyHistory()
    }, 500)
  }

  /**
   * 撤销最近一次剧情树或事件修改。
   * @returns 是否成功恢复上一份编辑快照。
   */
  function undoEditHistory(): boolean {
    const st = getStoryTree()
    if (!st) return false
    commitEventPropertyHistory()
    const result = history.undo(st, resolveHistoryEventList, applyCustomUiListSnapshot)
    if (!result) return false
    applyHistorySelection(result.selection, result.domain, result.restoredEventList)
    workspace.markProjectDirty()
    return true
  }

  /**
   * 重做最近一次被撤销的剧情树或事件修改。
   * @returns 是否成功恢复下一份编辑快照。
   */
  function redoEditHistory(): boolean {
    const st = getStoryTree()
    if (!st) return false
    const result = history.redo(st, resolveHistoryEventList, applyCustomUiListSnapshot)
    if (!result) return false
    applyHistorySelection(result.selection, result.domain, result.restoredEventList)
    workspace.markProjectDirty()
    return true
  }

  /**
   * 同步当前工程内所有剧情的结构事件，确保测试、保存和发布读取到最新缩进。
   * @returns 是否有剧情事件结构被调整。
   */
  function normalizeAllSceneEventStructures(): boolean {
    const st = getStoryTree()
    if (!st) return false
    let changed = false
    for (const map of storyTreeList.value) {
      const list = ensureSceneEventList(st, map as MapInfo)
      const mapId = String(map.Id)
      const normalizedText = normalizeSceneTextChoiceBlocks(list, mapId)
      const normalizedButton = normalizeSceneButtonChoiceBlocks(list, mapId)
      const normalizedIf = normalizeSceneIfBlocks(list, mapId)
      const normalizedLoop = normalizeSceneLoopBlocks(list, mapId)
      const normalizedAudio = normalizeSceneAudioResourcePaths(list)
      changed = normalizedText || normalizedButton || normalizedIf || normalizedLoop || normalizedAudio || changed
    }
    syncEventStructureState(selectedSceneEvents.value)
    if (changed) {
      bumpSceneStructureRevision()
      workspace.markProjectDirty()
    }
    return changed
  }

  function clearEventSelection(): void {
    selectedAddPlaceholder.value = null
    selectedEventIndices.value = new Set()
    selectedEventIndex.value = null
    selectedEvent.value = null
    selectedEventAnchorIndex.value = null
  }

  function selectCustomUi(selection: CustomUiSelection): void {
    const data = customUiList.value[selection.uiIndex]
    if (!data) return
    selectedCustomUi.value = {
      uiIndex: selection.uiIndex,
      itemIndex: selection.itemIndex,
      eventSource: selection.eventSource,
    }
    selectedSceneId.value = null
    clearEventSelection()
    syncEventStructureState(selectedEventList.value)
    bumpSceneStructureRevision(true)
  }

  function selectCustomUiRoot(uiIndex: number): void {
    selectCustomUi({ uiIndex, itemIndex: null, eventSource: 'none' })
  }

  function selectCustomUiLoadEvent(uiIndex: number): void {
    selectCustomUi({ uiIndex, itemIndex: null, eventSource: 'load' })
  }

  function selectCustomUiAfterEvent(uiIndex: number): void {
    selectCustomUi({ uiIndex, itemIndex: null, eventSource: 'after' })
  }

  function selectCustomUiControl(uiIndex: number, itemIndex: number): void {
    const item = customUiList.value[uiIndex]?.ControlList[itemIndex]
    if (!item) return
    selectCustomUi({
      uiIndex,
      itemIndex,
      eventSource: item.Type === CustomUIItem.controlType.Button ? 'control' : 'none',
    })
  }

  /** 确保当前处于剧情上下文，并选中已有剧情；无当前选择时优先恢复工程记录，其次选择第一条剧情。 */
  function ensureStorySelection(): void {
    const list = storyTreeList.value
    if (!list.length) {
      selectedCustomUi.value = null
      selectedSceneId.value = null
      return
    }
    const st = getStoryTree()
    const currentId = selectedSceneId.value
    const preferredId = st?.SelectMapIndex ?? currentId ?? 0
    const nextId = list.some((item) => item.Id === currentId)
      ? currentId!
      : list.some((item) => item.Id === preferredId)
        ? preferredId
        : list[0]!.Id
    selectScene(nextId)
  }

  /** 新建默认高级 UI，并选中它。 */
  function createDefaultCustomUi(recordHistory = true): CustomUIData | null {
    const before = recordHistory ? cloneCustomUiListSnapshot() : null
    const ui = new CustomUIData()
    ui.Name = 'UI界面'
    customUiList.value.push(ui)
    notifyCustomUiChanged()
    selectCustomUiRoot(customUiList.value.length - 1)
    if (before) {
      commitCustomUiHistory(before, '新增高级UI')
    }
    return ui
  }

  /** 确保当前处于高级 UI 上下文；没有 UI 时创建默认 UI，没有有效选择时选中第一个 UI。 */
  function ensureCustomUiSelection(): void {
    if (!customUiList.value.length) {
      createDefaultCustomUi()
      return
    }
    const selection = selectedCustomUi.value
    const data = selection ? customUiList.value[selection.uiIndex] : null
    const hasValidControl = selection?.itemIndex === null || data?.ControlList[selection?.itemIndex ?? -1]
    if (selection && data && hasValidControl) {
      selectCustomUi(selection)
      return
    }
    selectCustomUiRoot(0)
  }

  /** 标记高级 UI 属性发生变化，并刷新事件列表、属性面板和预览。 */
  function notifyCustomUiChanged(): void {
    customUiRevision.value++
    selectedEvent.value = selectedEventIndex.value === null ? null : (selectedEventList.value[selectedEventIndex.value] ?? null)
  }

  /* ——— 侦听器：初始化与同步 ——— */

  /** 切换选中场景时重置事件选择和折叠 */
  watch(selectedSceneId, (newId) => {
    const st = getStoryTree()
    if (st && newId !== null) {
      st.SelectMapIndex = newId
    }
    expandSceneAncestors(newId)
    applySelectedEventIndices(new Set(), null, null)
    syncEventStructureState(selectedSceneEvents.value)
    bumpSceneStructureRevision()
  }, { flush: 'sync' })

  /** 确保场景事件列表已挂到 storyTree */
  watch(
    () => [selectedSceneId.value, storyStructureRevision.value] as const,
    () => {
      const st = getStoryTree()
      const map = selectedMap.value
      if (!st || !map) return
      const list = ensureSceneEventList(st, map as MapInfo)
      const normalizedText = normalizeSceneTextChoiceBlocks(list, String(map.Id))
      const normalizedButton = normalizeSceneButtonChoiceBlocks(list, String(map.Id))
      const normalizedIf = normalizeSceneIfBlocks(list, String(map.Id))
      const normalizedLoop = normalizeSceneLoopBlocks(list, String(map.Id))
      const normalized = normalizedText || normalizedButton || normalizedIf || normalizedLoop
      syncEventStructureState(list)
      if (normalized) {
        bumpSceneStructureRevision()
      }
      assertCurrentSceneEventReferences('watch:ensureSceneEventList')
    },
    { immediate: true },
  )

  /** 工程场景列表变化时初始化选中 */
  watch(
    () => project.value?.Storys(),
    (list) => {
      const st = getStoryTree()
      if (!list?.length) {
        if (st) {
          st.SelectMapIndex = 0
        }
        selectedSceneId.value = null
        expandedSceneKeys.value = []
        return
      }
      const preferredId = st?.SelectMapIndex ?? 0
      const fallbackId = (list[0] as MapInfo).Id
      const nextId = list.some((m: MapInfo) => m.Id === preferredId) ? preferredId : fallbackId
      if (selectedSceneId.value === null || !list.some((m: MapInfo) => m.Id === selectedSceneId.value)) {
        selectedSceneId.value = nextId
      } else if (st && st.SelectMapIndex !== selectedSceneId.value) {
        st.SelectMapIndex = selectedSceneId.value
      }
      expandedSceneKeys.value = st?.rootMaps.map((m: MapInfo) => m.Id) ?? []
      expandSceneAncestors(selectedSceneId.value)
    },
    { immediate: true },
  )

  watch(
    () => project.value,
    () => {
      cancelPendingEventHistory()
      history.clear()
    },
  )

  return {
    /* 状态 */
    selectedSceneId,
    selectedEventIndex,
    selectedEventIndices,
    selectedEventAnchorIndex,
    selectedAddPlaceholder,
    selectedCustomUi,
    selectedEventPropertyKey,
    canShowSelectedEventProperty,
    formatEventPropertyEmptyHint,
    eventClipboard,
    hasEventClipboard,
    storyClipboard,
    hasStoryClipboard,
    sceneEventsRevision,
    sceneStructureRevision,
    scenePreviewRevision,
    sceneEventLabelRevision,
    storyStructureRevision,
    customUiRevision,
    collapsedFoldIndices,
    expandedSceneKeys,
    storyDragFromId,

    /* 计算属性 */
    project,
    storyTreeList,
    storyParentMap,
    selectedMap,
    selectedSceneEvents,
    selectedEventList,
    customUiList,
    selectedCustomUiData,
    selectedCustomUiItem,
    selectedEvent,
    canUndoEditHistory,
    canRedoEditHistory,
    storyTreeData,

    /* 方法 */
    sceneTreeLabel,
    selectScene,
    selectEventAtIndex,
    selectEventWithModifiers,
    selectAddPlaceholder,
    toggleEventFold,
    expandSceneAncestors,
    addStoryMap,
    addChildStoryMap,
    renameScene,
    deleteCurrentStoryMap,
    canDeleteStoryMap,
    copyStoryMap,
    cutStoryMap,
    pasteStoryMap,
    handleStoryDrop,
    allowStoryDrop,
    insertEventFromPalette,
    insertEventBlock,
    insertEventObject,
    insertEventObjects,
    importBasicTextRows,
    importAdvancedTextRows,
    deleteSelectedEvents,
    copySelectedEvents,
    cutSelectedEvents,
    pasteClipboardEvents,
    clearEventClipboard,
    canPasteAtSelection,
    validatePasteAtSelection,
    canMoveSelectedEventsTo,
    moveSelectedEvents,
    touchSceneEvents,
    beginEventPropertyHistory,
    commitEventPropertyHistory,
    notifySceneEventValueChanged,
    undoEditHistory,
    redoEditHistory,
    normalizeAllSceneEventStructures,
    selectCustomUiRoot,
    selectCustomUiLoadEvent,
    selectCustomUiAfterEvent,
    selectCustomUiControl,
    ensureStorySelection,
    createDefaultCustomUi,
    ensureCustomUiSelection,
    cloneCustomUiListSnapshot,
    commitCustomUiHistory,
    notifyCustomUiChanged,
    ensureSceneEventList,
    getTextChoiceBlockChoices,
    updateTextChoiceContent,
    syncTextChoiceBlock,
    syncButtonChoiceBlock,
    syncIfBlock,
    syncLoopBlock,
  }
})
